import { useState, useCallback, useRef, useMemo, useEffect, type JSX } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  type IGetRowsParams,
  type ValueFormatterParams,
} from 'ag-grid-community';
import { Search, Boxes } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../hooks/useApp';
import { SELECTION_COLUMN_DEF, emptyFormatter } from '../../lib/gridUtils';
import { reportDatasourceFailure } from '../../lib/agGridErrorTracker';
import ArbiSelect from '../../components/ui/ArbiSelect';
import ExportButton from '../../components/ui/ExportButton';

ModuleRegistry.registerModules([AllCommunityModule]);

interface InventoryRow {
  isg_sku?: string | null;
  title?: string | null;
  location?: string | null;
  quantity?: number | null;
  unit_cost?: number | null;
  distributor_name?: string | null;
  po_number?: string | null;
  po_id?: string | null;
  received_at?: string | null;
}

type GenericSupabaseRpc = (
  name: string,
  args: Record<string, unknown>
) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
const callRpc = supabase.rpc.bind(supabase) as unknown as GenericSupabaseRpc;

// ── Formatting ──────────────────────────────────────────────────────────────

function fmt$(v: number | string | null | undefined): string {
  if (v == null || v === '') return '\u2014';
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '\u2014';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

// ── Location config ─────────────────────────────────────────────────────────

interface LocationConfig {
  label: string;
  cls: string;
}

const LOCATION_CONFIG: Record<string, LocationConfig> = {
  at_distributor: { label: 'At Distributor', cls: 'bg-surface-3 text-ink-secondary' },
  in_transit: { label: 'In Transit', cls: 'bg-info/15 text-info' },
  at_warehouse: { label: 'At Warehouse', cls: 'bg-accent/15 text-accent' },
  fba_inbound: { label: 'FBA Inbound', cls: 'bg-warning/15 text-warning' },
  fba_live: { label: 'FBA Live', cls: 'bg-success/15 text-success' },
  fbm_listed: { label: 'FBM Listed', cls: 'bg-success/15 text-success' },
  out_of_stock: { label: 'Out of Stock', cls: 'bg-danger/15 text-danger' },
  quarantine: { label: 'Quarantine', cls: 'bg-warning/15 text-warning' },
  disposed: { label: 'Disposed', cls: 'bg-surface-3 text-ink-muted' },
};

const LOCATION_TABS = [
  { key: 'all', label: 'All' },
  { key: 'at_distributor', label: 'At Distributor' },
  { key: 'in_transit', label: 'In Transit' },
  { key: 'at_warehouse', label: 'At Warehouse' },
  { key: 'fba_inbound', label: 'FBA Inbound' },
  { key: 'fba_live', label: 'FBA Live' },
  { key: 'fbm_listed', label: 'FBM Listed' },
  { key: 'out_of_stock', label: 'Out of Stock' },
  { key: 'quarantine', label: 'Quarantine' },
];

// ── Inventory Page ──────────────────────────────────────────────────────────

export default function Inventory(): JSX.Element {
  const { distributors } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const gridRef = useRef<AgGridReact<InventoryRow> | null>(null);
  const gridApiRef = useRef<GridApi<InventoryRow> | null>(null);

  const [searchText, setSearchText] = useState<string>('');
  const [locationFilter, setLocationFilter] = useState<string>(
    () => searchParams.get('location') || 'all'
  );
  const [distributorFilter, setDistributorFilter] = useState<string>('');
  const [totalCount, setTotalCount] = useState<number>(0);

  const distOptions = [
    { value: '', label: 'All distributors' },
    ...distributors.map((d) => ({ value: d.id, label: d.name })),
  ];

  const stateRef = useRef<{
    searchText: string;
    locationFilter: string;
    distributorFilter: string;
  }>({ searchText, locationFilter, distributorFilter });
  stateRef.current = { searchText, locationFilter, distributorFilter };

  const getRowsFn = useCallback(async (params: IGetRowsParams): Promise<void> => {
    const { startRow, endRow, successCallback, failCallback } = params;
    const { searchText: st, locationFilter: lf, distributorFilter: df } = stateRef.current;

    try {
      const { data, error } = await callRpc('get_inventory_queue', {
        p_location: lf === 'all' ? null : lf,
        p_distributor_id: df || null,
        p_search: st.trim() || null,
        p_limit: endRow - startRow,
        p_offset: startRow,
      });
      if (error) throw new Error(error.message);

      const rows = (data as InventoryRow[]) || [];
      // Estimate total for now (RPC doesn't return total_count)
      const count =
        startRow === 0
          ? rows.length < endRow - startRow
            ? rows.length
            : rows.length + 100
          : rows.length;
      if (startRow === 0) setTotalCount(rows.length);
      successCallback(rows, count);
    } catch (e) {
      reportDatasourceFailure(
        'Inventory',
        { rpcName: 'get_inventory_queue', params: { startRow, endRow } },
        e,
        () => failCallback()
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onGridReady = useCallback(
    (params: GridReadyEvent<InventoryRow>): void => {
      gridApiRef.current = params.api;
      params.api.setGridOption('datasource', { getRows: getRowsFn });
    },
    [getRowsFn]
  );

  useEffect(() => {
    if (gridApiRef.current) {
      gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
    }
  }, [searchText, locationFilter, distributorFilter, getRowsFn]);

  // ── Cell renderers ──────────────────────────────────────────

  const SkuCell = useCallback(
    (p: ICellRendererParams<InventoryRow>): JSX.Element | null => {
      if (!p.data) return null;
      const sku = p.data.isg_sku;
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (sku) navigate(`/catalog/products/${sku}`);
          }}
          className="font-mono text-accent hover:text-accent-hover transition-colors cursor-pointer"
        >
          {sku}
        </button>
      );
    },
    [navigate]
  );

  const LocationCell = useCallback((p: ICellRendererParams<InventoryRow>): JSX.Element | null => {
    if (!p.data) return null;
    const loc = p.data.location ?? 'at_warehouse';
    const cfg = LOCATION_CONFIG[loc] || LOCATION_CONFIG.at_warehouse;
    return (
      <span className={clsx('px-1.5 py-0.5 rounded text-2xs font-medium', cfg.cls)}>
        {cfg.label}
      </span>
    );
  }, []);

  const POCell = useCallback(
    (p: ICellRendererParams<InventoryRow>): JSX.Element | null => {
      const poNumber = p.data?.po_number;
      const poId = p.data?.po_id;
      if (!poNumber) return null;
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (poId) navigate(`/purchasing/purchase-orders/${poId}`);
          }}
          className="font-mono text-accent hover:text-accent-hover transition-colors cursor-pointer text-xs"
        >
          {poNumber}
        </button>
      );
    },
    [navigate]
  );

  // ── Column defs ─────────────────────────────────────────────

  const columnDefs = useMemo<ColDef<InventoryRow>[]>(
    () =>
      [
        {
          headerName: 'ISG SKU',
          field: 'isg_sku',
          width: 120,
          pinned: 'left',
          suppressMovable: true,
          resizable: false,
          cellRenderer: SkuCell,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Title',
          field: 'title',
          flex: 1,
          minWidth: 180,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Location',
          field: 'location',
          width: 140,
          cellRenderer: LocationCell,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
          headerName: 'Qty',
          field: 'quantity',
          width: 80,
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            fontFamily: "'JetBrains Mono', monospace",
          },
          headerClass: 'ag-right-aligned-header',
        },
        {
          headerName: 'Unit Cost',
          field: 'unit_cost',
          width: 100,
          valueFormatter: (p: ValueFormatterParams<InventoryRow>) => fmt$(p.value as number | null),
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            fontFamily: "'JetBrains Mono', monospace",
          },
          headerClass: 'ag-right-aligned-header',
        },
        {
          headerName: 'Distributor',
          field: 'distributor_name',
          width: 130,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'PO',
          field: 'po_number',
          width: 130,
          cellRenderer: POCell,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Received',
          field: 'received_at',
          width: 110,
          valueFormatter: (p: ValueFormatterParams<InventoryRow>) =>
            relativeTime(p.value as string | null),
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            fontFamily: "'JetBrains Mono', monospace",
            color: 'var(--ink-muted)',
          },
        },
      ] as ColDef<InventoryRow>[],
    [SkuCell, LocationCell, POCell]
  );

  const defaultColDef = useMemo<ColDef<InventoryRow>>(
    () => ({
      sortable: true,
      resizable: true,
      sortingOrder: ['asc', 'desc', null],
      unSortIcon: true,
      headerClass: 'ag-left-aligned-header',
      cellStyle: { textAlign: 'left' },
      valueFormatter: emptyFormatter,
    }),
    []
  );

  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: 'var(--surface-0)',
        headerBackgroundColor: 'var(--surface-1)',
        headerTextColor: 'var(--ink-muted)',
        foregroundColor: 'var(--ink-primary)',
        borderColor: 'var(--border)',
        rowHoverColor: 'var(--surface-3)',
        headerFontSize: 10,
        fontSize: 12,
        spacing: 4,
      }),
    []
  );

  return (
    <div className="flex flex-col h-full -m-4">
      {/* Toolbar */}
      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h1 className="text-base font-semibold text-ink-bright">Inventory</h1>
            {totalCount > 0 && (
              <span className="text-xs text-ink-muted">
                {totalCount} {totalCount === 1 ? 'item' : 'items'}
              </span>
            )}
          </div>
          <ExportButton gridApiRef={gridApiRef} fileName="arbi-inventory" />
        </div>
        <div className="flex items-center gap-2">
          {/* Location filter tabs */}
          <div className="flex items-center bg-surface-2 rounded-md p-0.5 gap-0.5 border border-border-subtle overflow-x-auto">
            {LOCATION_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setLocationFilter(tab.key)}
                aria-pressed={locationFilter === tab.key}
                className={clsx(
                  'px-2 py-1 rounded text-2xs font-medium whitespace-nowrap transition-colors cursor-pointer',
                  locationFilter === tab.key
                    ? 'bg-accent/15 text-accent'
                    : 'text-ink-muted hover:text-ink-secondary'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="ml-auto flex items-center gap-2">
            {/* Distributor filter */}
            <ArbiSelect
              value={distributorFilter}
              options={distOptions}
              onChange={setDistributorFilter}
              className="w-40"
              size="xs"
            />
            {/* Search */}
            <div className="relative w-60">
              <Search
                size={12}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted"
              />
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search SKU or title..."
                aria-label="Search inventory by SKU or title"
                className="arbi-input pl-8 pr-3 py-1 text-xs w-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 p-2" style={{ minHeight: 0 }}>
        <AgGridReact
          ref={gridRef}
          theme={gridTheme}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowModelType="infinite"
          cacheBlockSize={100}
          maxBlocksInCache={50}
          selectionColumnDef={SELECTION_COLUMN_DEF}
          rowSelection={{
            mode: 'multiRow',
            enableClickSelection: false,
            checkboxes: true,
            headerCheckbox: false,
          }}
          onGridReady={onGridReady}
          animateRows={false}
          headerHeight={36}
          rowHeight={42}
          suppressCellFocus={true}
          suppressHorizontalScroll={true}
          noRowsOverlayComponent={() => (
            <div className="flex flex-col items-center justify-center h-full">
              <Boxes size={48} className="text-ink-muted mb-3" />
              <p className="text-sm text-ink-muted">
                {locationFilter !== 'all' || searchText
                  ? 'No items match your filters'
                  : 'No inventory yet \u2014 inventory populates automatically when POs are received'}
              </p>
            </div>
          )}
        />
      </div>
    </div>
  );
}
